import { X } from 'lucide-react'
import React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'

import { PinnedMessage } from '@/types/chat.type'

interface ViewPinnedMessagesDialogProps {
  pinnedMessages: PinnedMessage[]
  onClose: () => void
  onScrollToMessage?: (messageId: number) => void
  onUnpinMessage?: (messageId: number) => void
  formatTimestamp?: (timestamp: string) => string
}

export const ViewPinnedMessagesDialog: React.FC<ViewPinnedMessagesDialogProps> = ({
  pinnedMessages,
  onClose,
  onScrollToMessage,
  onUnpinMessage,
  formatTimestamp,
}) => {
  const handleMessageClick = (messageId: number) => {
    if (onScrollToMessage) {
      onScrollToMessage(messageId)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-[10003] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Pinned Messages</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {pinnedMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
                  />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">No pinned messages yet</p>
              <p className="text-gray-400 text-xs mt-1">Pin important messages to keep them at your fingertips</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pinnedMessages.map(pinnedMessage => (
                <div
                  key={pinnedMessage.id}
                  className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleMessageClick(pinnedMessage.message.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      {/* Message sender info - Top */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-gray-700">
                          {pinnedMessage.message.sender.firstName} {pinnedMessage.message.sender.lastName}
                        </span>
                        {formatTimestamp && pinnedMessage.message.createdAt && (
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(pinnedMessage.message.createdAt)}
                          </span>
                        )}
                      </div>

                      {/* Message content */}
                      <div
                        className="text-xs text-gray-600 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtmlValue(pinnedMessage.message.content) }}
                      />

                      {/* Pinned by info - Bottom */}
                      {pinnedMessage.pinnedByUser && (
                        <div className="text-xs text-gray-500 mt-1">
                          Pinned by {pinnedMessage.pinnedByUser.firstName} {pinnedMessage.pinnedByUser.lastName}
                        </div>
                      )}
                    </div>

                    {/* Unpin button */}
                    {onUnpinMessage && (
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          onUnpinMessage(pinnedMessage.message.id)
                        }}
                        className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Unpin message"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
